import React, { useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'
import {
  EDatasetKey,
  EParamChannelTypeGlobal,
  EReportColumnHeader,
  EReportingTimeWindow,
  IReportAdvertisersTable,
} from '@unfoldrtech/portal-mic/dist/src/models/core'

import { ReportPageTemplateLayout } from '../../components/ReportPageTemplateLayout'
import { ReportingAdvertisersChartWrapper } from './ReportingAdvertisersChartWrapper'
import { TReportExportType, TReportingFilters } from '../../../../models'
import { AppContext } from '../../../../models/contexts'
import {
  selectAdvertisersTableReportingFilters,
  setIsReportingV3,
} from '../../../../store/reportingFilters'
import {
  NOT_AVAILABLE,
  exportFileDateFormat,
} from '../../../../utils/constants'
import { EXPORT_PAGE_LIMIT, exportTableDataToFile } from '../../helpers'
import { useGetDatasetKeyTableWhitelist } from '../../hooks/use-get-dataset-key-table-whitelist'
import { ReportingAdvertisersTableWrapper } from './ReportingAdvertisersTableWrapper'
import { TDatasetWhitelistKey } from '../../models'
import { generateCorrelationId, getTimezone } from '../../../../utils/helpers'
import { coreAPIV3 } from '../../../../services/coreAPIV3'
import { useGetRetailerAndAdvertiserIds } from '../../hooks/use-get-retailer-and-advertiser-ids'
import { ErrorToast } from '../../../ToastCard/ErrorToast'
import { SuccessToast } from '../../../ToastCard/SuccessToast'

export const ReportingAdvertisersTemplate = ({
  isHome,
}: {
  isHome?: boolean
}) => {
  let pageTitle = 'reporting.header.advertisers'
  pageTitle = isHome ? 'home' : pageTitle

  const intl = useIntl()
  const dispatch = useDispatch()

  const [appContext] = useContext(AppContext)
  const filters = useSelector(selectAdvertisersTableReportingFilters)
  const { retailerId, advertiserId } = useGetRetailerAndAdvertiserIds()

  const [isExporting, setIsExporting] = useState(false)
  const [showExportErrorToast, setShowExportErrorToast] = useState(false)
  const [showExportCappedToast, setShowExportCappedToast] = useState(false)

  const [tableData, setTableData] = useState<IReportAdvertisersTable>()

  const whitelistData = useGetDatasetKeyTableWhitelist({
    key:
      (tableData?.datasetKey as TDatasetWhitelistKey) || EDatasetKey.Advertiser,
  })

  const onExportData = async (
    { startDate: start, endDate: end }: TReportingFilters,
    type: TReportExportType
  ) => {
    const orgName =
      appContext.userOrg?.name ?? appContext.userOrg?.type ?? NOT_AVAILABLE
    const translatedPageTitle = intl
      .formatMessage({ id: pageTitle })
      .replace(/[-]+/g, '')
      .replace(/[ ]+/g, '_')
    const sDate = dayjs(start).format(exportFileDateFormat)
    const eDate = dayjs(end).format(exportFileDateFormat)

    const fileName = `${orgName}_${translatedPageTitle}_${sDate}_${eDate}`
    setIsExporting(true)
    setShowExportErrorToast(false)
    setShowExportCappedToast(false)
    try {
      const res = await coreAPIV3.retailerReporting().getRetailerAdvertisersReportTable(
        retailerId,
        generateCorrelationId(),
        advertiserId,
        filters.sortBy as EReportColumnHeader,
        filters.sortOrder,
        filters.timeWindow as EReportingTimeWindow,
        start,
        end,
        filters.tzZone || getTimezone(),
        EXPORT_PAGE_LIMIT,
        0,
        filters.filterBy,
        filters.filterOperation,
        filters.filterValue,
        filters.name ? encodeURI(filters.name) : undefined,
        // @ts-expect-error: fix in PORT-2535
        filters.status,
        filters.channelType as EParamChannelTypeGlobal | undefined
      )

      const rows = res.data.table?.rows ?? []

      exportTableDataToFile({
        tableDataRows: rows,
        tableHeaders: whitelistData,
        fileName,
        fileType: type,
      })

      if (rows.length >= EXPORT_PAGE_LIMIT) {
        setShowExportCappedToast(true)
      }
    } catch {
      setShowExportErrorToast(true)
    } finally {
      setIsExporting(false)
    }
  }

  const onTableDataChange = (advertiserTableData: IReportAdvertisersTable) => {
    setTableData(advertiserTableData)
  }

  useEffect(() => {
    dispatch(setIsReportingV3({ IsReportingV3: true }))
  }, [])

  return (
    <>
      <ReportPageTemplateLayout
        title={pageTitle}
        isExportDisabled={isExporting || !tableData?.datasetKey}
        chartWrapper={<ReportingAdvertisersChartWrapper />}
        tableWrapper={
          <ReportingAdvertisersTableWrapper
            isHome={isHome}
            onTableDataChange={onTableDataChange}
          />
        }
        onExportData={onExportData}
      />
      <SuccessToast
        show={showExportCappedToast}
        onClose={() => setShowExportCappedToast(false)}
        translationKey="reporting.export.capped"
      />
      <ErrorToast
        show={showExportErrorToast}
        onClose={() => setShowExportErrorToast(false)}
        translationKey="reporting.export.failed"
      />
    </>
  )
}
